import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  FaBroom,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaDatabase,
  FaBarcode,
  FaDoorOpen,
  FaDownload,
  FaFilePdf,
  FaLaptopCode,
  FaPause,
  FaPlay,
  FaPowerOff,
  FaSave,
  FaServer,
  FaSignInAlt,
  FaSyncAlt,
  FaTools,
  FaTimesCircle,
  FaTruck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import { getAppDateString, getAppDateTimeString } from "../utils/date";
import SealAddCard from "./SealAddCard";
import SealBarcodeCard from "./SealBarcodeCard";
import ArchiveFilesDialog from "./ArchiveFilesDialog";
import LabelSearchDialog from "./LabelSearchDialog";
import FlipkartCropDialog from "./FlipkartCropDialog";

const defaultOverview = {
  server_runtime: "Node.js / Express",
  database: "MySQL",
  python_required_on_server: false,
  local_worker_runtime: "Existing local Myntra Python app only",
  worker_token_configured: false,
  command_execution_enabled: false,
  command_execution_phase: "phase2_visibility",
  phase4_risky_commands_enabled: false,
  phase5_operator_commands_enabled: false,
  command_supported_types: [],
  command_stale_minutes: 10,
  app_time_zone: "Asia/Kolkata",
  app_date: "",
  app_datetime: "",
  workers: [],
  recent_events: [],
  recent_commands: [],
  cloud_accounts: [],
  cloud_schedules: [],
  recent_run_summaries: [],
  today_run_summary: [],
  db_tables: [],
  status_counts: {},
  endpoints: [],
};

const defaultDbBrowser = {
  table_name: "",
  label: "",
  machine_id: "",
  source_db_path: "",
  columns: [],
  searchable_columns: [],
  date_columns: [],
  quick_filters: [],
  rows: [],
  page: 1,
  page_size: 50,
  total: 0,
  total_pages: 1,
  sort_by: "",
  sort_dir: "DESC",
};

const pageBuildMarker = "myntra-browser-pc-local-probe-20260522";
const localProbeUrls = [
  "http://127.0.0.1:8765/myntra-local-worker.json",
  "http://localhost:8765/myntra-local-worker.json",
];
const showDashboardBody = false;
const dbEditorColors = {
  bg: "#1e2332",
  panel: "#252b3b",
  card: "#2d3448",
  accent: "#4a9eff",
  text: "#ecf0f1",
  muted: "#95a5a6",
  border: "#3d4460",
  header: "#16213e",
  even: "#2d3448",
  selected: "#3d5a80",
};
const toolbarButtonSx = {
  fontWeight: 900,
  color: "#f8fafc",
  borderColor: "rgba(248, 250, 252, 0.72)",
  bgcolor: "rgba(15, 23, 42, 0.22)",
  textShadow: "0 1px 1px rgba(0, 0, 0, 0.45)",
  "&:hover": {
    borderColor: "#ffffff",
    bgcolor: "rgba(15, 23, 42, 0.36)",
  },
  "& .MuiButton-startIcon, & svg": {
    color: "inherit",
  },
  "&.Mui-disabled": {
    color: "rgba(248, 250, 252, 0.72)",
    borderColor: "rgba(248, 250, 252, 0.36)",
    bgcolor: "rgba(15, 23, 42, 0.16)",
    WebkitTextFillColor: "rgba(248, 250, 252, 0.72)",
  },
  "&.Mui-disabled .MuiButton-startIcon, &.Mui-disabled svg": {
    color: "rgba(248, 250, 252, 0.72)",
  },
};
const toolbarPrimaryButtonSx = {
  fontWeight: 900,
  color: "#ffffff",
  bgcolor: "#1d4ed8",
  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.28)",
  "&:hover": {
    bgcolor: "#1e40af",
  },
  "& .MuiButton-startIcon, & svg": {
    color: "inherit",
  },
};

const commandCards = [
  {
    type: "health_check",
    title: "Health Check",
    icon: <FaPowerOff />,
    color: "primary",
    payload: {},
  },
  {
    type: "manual_schedule_run",
    title: "Run Schedule",
    icon: <FaPlay />,
    color: "success",
    payload: { scheduled_time: "manual", cutoff_time: "" },
  },
  {
    type: "login_profiles",
    title: "Login Profiles",
    icon: <FaSignInAlt />,
    color: "warning",
    payload: { profile_range: "1-15" },
  },
  {
    type: "open_profiles",
    title: "Open Profiles",
    icon: <FaDoorOpen />,
    color: "info",
    payload: { profile_range: "1-15", dry_run: false },
  },
  {
    type: "start_scheduler",
    title: "Start Scheduler",
    icon: <FaPlay />,
    color: "success",
    payload: {},
  },
  {
    type: "stop_scheduler",
    title: "Stop Scheduler",
    icon: <FaPause />,
    color: "warning",
    payload: {},
  },
  {
    type: "merge_all_pdfs",
    title: "Merge All PDFs",
    icon: <FaFilePdf />,
    color: "primary",
    payload: { print_after_merge: false },
  },
  {
    type: "repair_recover_pdfs",
    title: "Repair PDFs",
    icon: <FaTools />,
    color: "secondary",
    payload: { quarantine_raw: true },
  },
  {
    type: "intransit_download",
    title: "In-Transit",
    icon: <FaTruck />,
    color: "info",
    payload: {},
  },
  {
    type: "intransit_otc",
    title: "OTC",
    icon: <FaSyncAlt />,
    color: "primary",
    payload: {},
  },
  {
    type: "cleanup_profiles",
    title: "Clean Profiles",
    icon: <FaBroom />,
    color: "secondary",
    payload: {},
  },
  {
    type: "backup_local_db",
    title: "Backup DB",
    icon: <FaSave />,
    color: "primary",
    payload: {},
  },
  {
    type: "sync_local_db_summary",
    title: "Sync DB Summary",
    icon: <FaDatabase />,
    color: "success",
    payload: {},
  },
  {
    type: "sync_local_db_full",
    title: "Sync Full DB",
    icon: <FaDatabase />,
    color: "success",
    payload: {},
  },
];

const localToolsMenu = [
  { type: "open_login_account_dialog", title: "Login Account", icon: "🔑" },
  { type: "open_login_extra_profile_dialog", title: "Login Extra Profile", icon: "🔑" },
  { type: "open_seal_barcode_pdf_dialog", title: "Seal Barcode PDF", icon: "🖨" },
  { type: "open_tracking_search_dialog", title: "Search & Merge PDFs", icon: "🔎" },
  { type: "open_merge_all_dialog", title: "Merge All Account PDFs", icon: "📎" },
  { type: "repair_recover_pdfs", title: "Repair / Recovery PDFs", icon: "" },
  { type: "open_today_pdf_list_dialog", title: "PDF / SKU Excel List Today", icon: "" },
  { type: "unmerge_last_bulk_merge", title: "Unmerge Last Bulk Merge", icon: "" },
  { type: "open_intransit_download_dialog", title: "In-Transit Download", icon: "🚚" },
  { divider: true },
  { type: "open_seal_add_dialog", title: "Add Seals", icon: "🏷" },
  { type: "check_seals_now", title: "Check Seals", icon: "🔍" },
  { type: "open_merge_pdfs_dialog", title: "Merge PDFs", icon: "📎" },
  { divider: true },
  { type: "open_telegram_settings_dialog", title: "Telegram Settings", icon: "📱" },
];

const localToolbarActions = [
  { type: "open_label_search_web", title: "Search", icon: "🔎" },
  { type: "open_sku_menu_dialog", title: "SKU", icon: "📊" },
  { type: "open_archive_files_web", title: "Files", icon: "" },
  { type: "open_merge_all_dialog", title: "Merge All", icon: "📎" },
  { type: "open_manual_run_dialog", title: "Run Now", icon: "▶" },
];

const defaultTrackingSearch = {
  machine_id: "",
  rows: [],
  found: 0,
  ready: 0,
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function statusColor(status) {
  const normalized = String(status || "").toLowerCase();
  if (["online", "running", "ready", "done"].includes(normalized)) return "success";
  if (["warning", "idle", "pending"].includes(normalized)) return "warning";
  if (["error", "offline", "blocked", "failed"].includes(normalized)) return "error";
  return "default";
}

function prettyPayload(payload) {
  const entries = Object.entries(payload || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined);
  if (!entries.length) return "-";
  return entries.map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`).join(" | ");
}

function countProfiles(value) {
  const seen = new Set();
  String(value || "")
    .split(/[,\s;]+/)
    .map(part => part.trim())
    .filter(Boolean)
    .forEach(part => {
      if (part.includes("-")) {
        const [left, right] = part.split("-", 2).map(v => Number.parseInt(v, 10));
        if (Number.isInteger(left) && Number.isInteger(right)) {
          const step = right >= left ? 1 : -1;
          for (let n = left; step > 0 ? n <= right : n >= right; n += step) {
            if (n >= 1 && n <= 15) seen.add(n);
          }
        }
      } else {
        const n = Number.parseInt(part, 10);
        if (Number.isInteger(n) && n >= 1 && n <= 15) seen.add(n);
      }
    });
  return seen.size || 15;
}

function prettyResult(result, errorMessage) {
  if (errorMessage) return errorMessage;
  if (!result || !Object.keys(result).length) return "-";
  if (result.health) {
    return `ok: ${String(result.ok)} | db: ${result.health?.db?.text || "-"} | disk: ${result.health?.disk?.text || "-"} | lines: ${(result.lines || []).length}`;
  }
  if (result.lines_count || result.db || result.disk) {
    return `ok: ${String(result.ok)} | db: ${result.db || "-"} | disk: ${result.disk || "-"}`;
  }
  if (result.ok !== undefined && result.backup) {
    return `ok: ${String(result.ok)} | backup: ${result.backup.status || "-"}`;
  }
  if (result.ok !== undefined && result.stats) {
    return `ok: ${String(result.ok)} | orders: ${result.stats.orders || 0} | today: ${result.stats.today_success || 0}`;
  }
  if (result.table_count !== undefined && result.row_count !== undefined) {
    return `ok: ${String(result.ok)} | synced: ${result.table_count || 0} table(s), ${formatNumber(result.row_count || 0)} row(s)`;
  }
  if (result.action === "start_scheduler" || result.action === "stop_scheduler") {
    return `ok: ${String(result.ok)} | scheduler: ${result.scheduler_running_after ? "ON" : "OFF"}`;
  }
  if (result.mode === "local_ui") {
    return `ok: ${String(result.ok)} | opened: ${result.label || result.action || "-"}`;
  }
  if (result.merge_all) {
    const merge = result.merge_all || {};
    return `ok: ${String(result.ok)} | merge: ${merge.status || "-"} | pages: ${merge.actual_merged_pages || 0}/${merge.expected_input_pdfs || 0} | accounts: ${merge.accounts_ok || 0}/${merge.accounts_total || 0}`;
  }
  if (result.repair_recovery) {
    const totals = result.repair_recovery?.totals || {};
    return `ok: ${String(result.ok)} | repaired: ${totals.repaired || 0}/${totals.repair_checked || 0} | quarantined: ${totals.quarantined || 0} | errors: ${totals.errors || 0}`;
  }
  if (result.intransit) {
    const item = result.intransit || {};
    return `ok: ${String(result.ok)} | ${item.mode || "in-transit"}: ${item.status || "-"} | accounts: ${item.accounts_ok || 0}/${item.accounts_total || 0} | scan: ${item.scan_tasks_ok || 0}/${item.scan_tasks_total || 0}`;
  }
  if (result.plan && result.action) {
    return `ok: ${String(result.ok)} | dry-run: ${String(result.dry_run)} | ${result.action} | plan: ${result.plan.length || 0}`;
  }
  if (result.ok !== undefined && result.profile_cleanup) {
    return `ok: ${String(result.ok)} | cleanup: ${result.profile_cleanup.status || "-"}`;
  }
  if (result.queued !== undefined) {
    return `ok: ${String(result.ok)} | queued: ${result.queued || 0} | cutoff: ${result.cutoff || "-"} | blocked: ${(result.blocked || []).length}`;
  }
  if (result.results || result.ok_count !== undefined) {
    return `ok: ${String(result.ok)} | profiles: ${result.ok_count || 0}/${result.count || 0} | skipped: ${(result.skipped || []).length}`;
  }
  return prettyPayload(result);
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function todayDateString() {
  return getAppDateString();
}

function titleizeColumn(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
}

function shortCell(value, maxLength = 180) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    try {
      const json = JSON.stringify(value);
      return json.length > maxLength ? `${json.slice(0, maxLength)}...` : json;
    } catch {
      return String(value);
    }
  }
  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function clearEmptyFilters(filters) {
  return Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => String(value || "").trim())
  );
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function yesNoChip(value, label) {
  return (
    <Chip
      key={label}
      size="small"
      label={label}
      color={Number(value) ? "success" : "default"}
      variant={Number(value) ? "filled" : "outlined"}
      sx={{ mr: 0.5, mb: 0.5, fontWeight: 700 }}
    />
  );
}

function StatCard({ icon, title, value, tone = "primary", sub }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", minHeight: 112 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color: `${tone}.main`, fontSize: 24, display: "flex", mt: 0.25 }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.2, mt: 0.25 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function MyntraAutomationPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(defaultOverview);
  const [loading, setLoading] = useState(false);
  const [targetMachineId, setTargetMachineId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [cutoffTime, setCutoffTime] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [profileRange, setProfileRange] = useState("1-15");
  const [notes, setNotes] = useState("");
  const [queueing, setQueueing] = useState("");
  const [cancellingCommand, setCancellingCommand] = useState("");
  const [toolsAnchorEl, setToolsAnchorEl] = useState(null);
  const [sealAddOpen, setSealAddOpen] = useState(false);
  const [sealBarcodeOpen, setSealBarcodeOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [labelSearchOpen, setLabelSearchOpen] = useState(false);
  const [flipkartCropOpen, setFlipkartCropOpen] = useState(false);
  const [dbTables, setDbTables] = useState([]);
  const [dbBrowser, setDbBrowser] = useState(defaultDbBrowser);
  const [dbEditorOpen, setDbEditorOpen] = useState(false);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbTableName, setDbTableName] = useState("");
  const [dbSearch, setDbSearch] = useState("");
  const [dbSearchColumn, setDbSearchColumn] = useState("all");
  const [dbOnlyUnused, setDbOnlyUnused] = useState(false);
  const [dbSortBy, setDbSortBy] = useState("");
  const [dbSortDir, setDbSortDir] = useState("DESC");
  const [dbDateFilterOn, setDbDateFilterOn] = useState(false);
  const [dbDateColumn, setDbDateColumn] = useState("");
  const [dbDateFrom, setDbDateFrom] = useState(todayDateString());
  const [dbDateTo, setDbDateTo] = useState(todayDateString());
  const [dbPage, setDbPage] = useState(1);
  const [dbPageSize, setDbPageSize] = useState(50);
  const [dbColumnFilters, setDbColumnFilters] = useState({});
  const [dbColumnFilterDraft, setDbColumnFilterDraft] = useState("");
  const [dbSelectedRows, setDbSelectedRows] = useState({});
  const [dbColumnMenu, setDbColumnMenu] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingTerms, setTrackingTerms] = useState("");
  const [trackingPartial, setTrackingPartial] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingMerging, setTrackingMerging] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState(defaultTrackingSearch);
  const [trackingSelected, setTrackingSelected] = useState({});
  const [trackingLog, setTrackingLog] = useState([]);
  const [browserWorker, setBrowserWorker] = useState({ status: "checking", machine_id: "", machine_name: "", error: "" });

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/admin/myntra-automation/overview");
      setOverview({ ...defaultOverview, ...res.data });
    } catch (error) {
      console.error("Failed to load Myntra automation overview:", error);
      toast.error(error.response?.data?.error || "Failed to load Myntra automation page");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDbTables = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/myntra-automation/local-db/tables");
      const tables = res.data?.tables || [];
      setDbTables(tables);
      return tables;
    } catch (error) {
      console.error("Failed to load Myntra DB table list:", error);
      toast.error(error.response?.data?.error || "Failed to load DB table list");
      return [];
    }
  }, []);

  const loadDbTable = useCallback(async (overrides = {}) => {
    const tableName = overrides.tableName || dbTableName;
    if (!tableName) return;
    const page = overrides.page || dbPage;
    const pageSize = overrides.pageSize || dbPageSize;
    try {
      setDbLoading(true);
      const params = {
        page,
        page_size: pageSize,
        search: overrides.search ?? dbSearch,
        search_column: overrides.searchColumn ?? dbSearchColumn,
        sort_by: overrides.sortBy ?? dbSortBy,
        sort_dir: overrides.sortDir ?? dbSortDir,
        date_column: (overrides.dateFilterOn ?? dbDateFilterOn) ? (overrides.dateColumn ?? dbDateColumn) : "",
        date_from: overrides.dateFrom ?? dbDateFrom,
        date_to: overrides.dateTo ?? dbDateTo,
        only_unused: (overrides.onlyUnused ?? dbOnlyUnused) ? "1" : "",
        filters: JSON.stringify(clearEmptyFilters(overrides.filters ?? dbColumnFilters)),
      };
      const res = await api.get(`/api/admin/myntra-automation/local-db/tables/${tableName}/rows`, { params });
      const data = { ...defaultDbBrowser, ...res.data };
      setDbBrowser(data);
      setDbTableName(data.table_name || tableName);
      setDbPage(data.page || page);
      setDbPageSize(data.page_size || pageSize);
      setDbSortBy(data.sort_by || "");
      setDbSortDir(data.sort_dir || "DESC");
      setDbSelectedRows({});
      if (!dbSearchColumn || (dbSearchColumn !== "all" && !(data.searchable_columns || []).includes(dbSearchColumn))) {
        setDbSearchColumn("all");
      }
      if (!dbDateColumn && (data.date_columns || []).length) {
        setDbDateColumn(data.date_columns[0]);
      }
    } catch (error) {
      console.error("Failed to load DB table:", error);
      toast.error(error.response?.data?.error || "Failed to load DB table");
    } finally {
      setDbLoading(false);
    }
  }, [dbColumnFilters, dbDateColumn, dbDateFilterOn, dbDateFrom, dbDateTo, dbOnlyUnused, dbPage, dbPageSize, dbSearch, dbSearchColumn, dbSortBy, dbSortDir, dbTableName]);

  const detectBrowserWorker = useCallback(async () => {
    setBrowserWorker(current => ({ ...current, status: "checking", error: "" }));
    for (const url of localProbeUrls) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 1200);
      try {
        const res = await fetch(`${url}?t=${Date.now()}`, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!res.ok) continue;
        const data = await res.json();
        if (data?.ok && data?.app === "myntra_automation_suite" && data?.machine_id) {
          const worker = {
            status: "detected",
            machine_id: String(data.machine_id),
            machine_name: String(data.machine_name || data.machine_id),
            supported_command_types: Array.isArray(data.supported_command_types) ? data.supported_command_types : [],
            error: "",
          };
          setBrowserWorker(worker);
          setTargetMachineId(current => current || worker.machine_id);
          return;
        }
      } catch (_error) {
        clearTimeout(timeout);
      }
    }
    setBrowserWorker({
      status: "missing",
      machine_id: "",
      machine_name: "",
      error: "Local Myntra app not detected on this browser PC.",
    });
  }, []);

  useEffect(() => {
    loadOverview();
    loadDbTables();
    detectBrowserWorker();
    const timer = setInterval(loadOverview, 15000);
    return () => clearInterval(timer);
  }, [detectBrowserWorker, loadDbTables, loadOverview]);

  const onlineWorkers = useMemo(
    () => overview.workers.filter(worker => ["online", "running", "ready", "idle"].includes(String(worker.status || "").toLowerCase())).length,
    [overview.workers]
  );

  const pendingCommands = useMemo(
    () => overview.recent_commands.filter(command => command.status === "pending" || command.status === "running").length,
    [overview.recent_commands]
  );

  const supportedCommandTypes = useMemo(
    () => new Set(overview.command_supported_types || []),
    [overview.command_supported_types]
  );

  const activeSchedules = useMemo(
    () => overview.cloud_schedules.filter(schedule => Number(schedule.is_active)).length,
    [overview.cloud_schedules]
  );

  const dbStatsRows = useMemo(
    () => dbTables,
    [dbTables]
  );

  const targetOptions = useMemo(() => {
    const options = overview.workers.map(worker => ({
      value: worker.machine_id,
      label: worker.machine_name ? `${worker.machine_name} (${worker.machine_id})` : worker.machine_id,
      status: String(worker.status || "").toLowerCase(),
      supported: Array.isArray(worker.supported_command_types) && worker.supported_command_types.includes("tracking_pdf_search_merge_local"),
      isBrowserPc: false,
    }));
    if (browserWorker.machine_id && !options.some(option => option.value === browserWorker.machine_id)) {
      options.unshift({
        value: browserWorker.machine_id,
        label: `${browserWorker.machine_name || "This browser PC"} (${browserWorker.machine_id})`,
        status: "detected",
        supported: Array.isArray(browserWorker.supported_command_types) && browserWorker.supported_command_types.includes("tracking_pdf_search_merge_local"),
        isBrowserPc: true,
      });
    }
    return options;
  }, [browserWorker, overview.workers]);

  const selectedTargetWorker = useMemo(
    () => targetOptions.find(worker => worker.value === targetMachineId) || null,
    [targetMachineId, targetOptions]
  );

  const commandAllowed = (card) => overview.command_execution_enabled && supportedCommandTypes.has(card.type);

  const addTrackingLog = (message, tone = "info") => {
    setTrackingLog(current => [...current, { message, tone, at: getAppDateTimeString() }].slice(-80));
  };

  const openTrackingDialog = () => {
    setToolsAnchorEl(null);
    setTrackingDialogOpen(true);
  };

  const setTrackingRowsSelected = (rows) => {
    setTrackingSelected(Object.fromEntries(
      (rows || []).map(row => [row.row_key, Boolean(row.label_uploaded || row.invoice_uploaded)])
    ));
  };

  const mergeTrackingRows = async (rowsToMerge = null, machineId = "") => {
    const rows = rowsToMerge || trackingSearch.rows.filter(row => trackingSelected[row.row_key]);
    const rowKeys = rows.map(row => row.row_key).filter(Boolean);
    if (!rowKeys.length) {
      toast.info("Select at least one row.");
      return;
    }
    const commandType = "tracking_pdf_search_merge_local";
    if (!overview.command_execution_enabled || !supportedCommandTypes.has(commandType)) {
      toast.info("Local PC merge command is locked until guarded commands are enabled on server and local app.");
      return;
    }
    try {
      setTrackingMerging(true);
      const openMachineId = targetMachineId || browserWorker.machine_id || machineId || "";
      if (!openMachineId) {
        toast.info("Open Myntra local app on this browser PC, or select a target PC.");
        addTrackingLog("This browser PC local app was not detected. Start Myntra app on this PC, then retry.", "warn");
        return;
      }
      addTrackingLog(`Queued local PC merge for ${rowKeys.length} row(s) on ${openMachineId}.`);
      const res = await api.post("/api/admin/myntra-automation/commands", {
        command_type: commandType,
        target_machine_id: openMachineId,
        payload: {
          row_keys: rowKeys,
          selected_rows: rows,
          tracking_ids: trackingTerms.trim(),
          partial: trackingPartial,
          open_after_merge: true,
          fresh_download_if_missing: true,
        },
      });
      addTrackingLog(`Command #${res.data.command_id} queued. The merged PDF will open on ${openMachineId}.`, "success");
      toast.success(`Queued local merge #${res.data.command_id}`);
      await loadOverview();
    } catch (error) {
      console.error("Failed to queue local tracking PDF merge:", error);
      const message = error.response?.data?.error || "Failed to queue local tracking PDF merge";
      addTrackingLog(message, "error");
      toast.error(message);
    } finally {
      setTrackingMerging(false);
    }
  };

  const runTrackingSearch = async () => {
    const terms = trackingTerms.trim();
    if (!terms) {
      toast.info("Enter at least one tracking ID.");
      return;
    }
    const searchMachineId = targetMachineId || browserWorker.machine_id || "";
    if (!searchMachineId) {
      toast.info("Open Myntra local app on this browser PC, or select a target PC.");
      addTrackingLog("This browser PC local app was not detected. Search & Merge will not fall back to another PC automatically.", "warn");
      return;
    }
    try {
      setTrackingLoading(true);
      setTrackingLog([]);
      addTrackingLog(`${trackingPartial ? "Partial searching" : "Searching"} tracking IDs on ${searchMachineId}...`);
      const res = await api.post("/api/admin/myntra-automation/tracking-pdf-search", {
        terms,
        partial: trackingPartial,
        machine_id: searchMachineId,
      });
      const data = { ...defaultTrackingSearch, ...res.data };
      setTrackingSearch(data);
      setTrackingRowsSelected(data.rows);
      addTrackingLog(`Found ${data.found || 0} row(s) on ${data.machine_id || "local PC"}; ${data.ready || 0} row(s) have PDF paths in DB.`, data.ready ? "success" : "warn");
      for (const row of data.rows || []) {
        const bits = [
          row.label_path ? "Label path in DB" : "Label path missing",
          row.invoice_path ? "Invoice path in DB" : "Invoice path missing",
        ];
        addTrackingLog(`${row.tracking_id || row.tracking_id2 || row.order_id}: ${bits.join(" | ")}`, row.label_path || row.invoice_path ? "success" : "warn");
      }
      if (!trackingPartial && data.rows?.length) {
        const readyRows = data.rows.filter(row => row.label_path || row.invoice_path);
        if (readyRows.length) await mergeTrackingRows(readyRows, data.machine_id);
      }
    } catch (error) {
      console.error("Failed to search tracking PDFs:", error);
      const message = error.response?.data?.error || "Failed to search tracking PDFs";
      addTrackingLog(message, "error");
      toast.error(message);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleLocalToolAction = (item) => {
    if (!item) return;
    if (item.type === "open_tracking_search_dialog") {
      openTrackingDialog();
      return;
    }
    if (item.type === "open_label_search_web") {
      setLabelSearchOpen(true);
      return;
    }
if (item.type === "open_archive_files_web") {
      setFilesOpen(true);
      return;
    }
    queueCommand(item);
  };

  const queueCommand = async (card) => {
    if (!card) return;
    if (!overview.command_execution_enabled) {
      toast.info("Cloud commands are disabled. Use the local Myntra app for manual runs/logins.");
      return;
    }
    if (!commandAllowed(card)) {
      toast.info("This command is locked until guarded commands are enabled on server and local app.");
      return;
    }
    if (card.type === "manual_schedule_run" && !accountName.trim()) {
      toast.error("Enter an account name, or type ALL deliberately.");
      return;
    }

    const payload = {
      ...(card.payload || {}),
      account_name: accountName.trim(),
      cutoff_time: cutoffTime.trim(),
      scheduled_time: scheduledTime.trim() || card.payload?.scheduled_time || "",
      profile_range: profileRange.trim() || card.payload?.profile_range || "",
      profile_worker_count: countProfiles(profileRange),
      use_extra_profiles: true,
      notes: notes.trim(),
    };

    try {
      setQueueing(card.type);
      const res = await api.post("/api/admin/myntra-automation/commands", {
        command_type: card.type,
        target_machine_id: targetMachineId || null,
        payload,
      });
      toast.success(`Queued ${card.title} #${res.data.command_id}`);
      setToolsAnchorEl(null);
      await loadOverview();
    } catch (error) {
      console.error("Failed to queue command:", error);
      toast.error(error.response?.data?.error || "Failed to queue command");
    } finally {
      setQueueing("");
    }
  };

  const cancelCommand = async (command) => {
    try {
      setCancellingCommand(String(command.id));
      await api.post(`/api/admin/myntra-automation/commands/${command.id}/cancel`, {
        reason: "Cancelled from admin page",
      });
      toast.success(`Cancelled command #${command.id}`);
      await loadOverview();
    } catch (error) {
      console.error("Failed to cancel command:", error);
      toast.error(error.response?.data?.error || "Failed to cancel command");
    } finally {
      setCancellingCommand("");
    }
  };

  const openDbTable = async (tableName, sourceTables = null) => {
    const tableSource = sourceTables || (dbTables.length ? dbTables : dbStatsRows);
    const table = tableSource.find(item => item.table_name === tableName || item.name === tableName);
    const columns = table?.columns || [];
    const defaultSort = table?.default_sort && columns.some(column => column.name === table.default_sort)
      ? table.default_sort
      : columns[0]?.name || "";
    const defaultDir = table?.default_dir || "DESC";
    const firstDateColumn = (table?.date_columns || [])[0] || "";
    setDbTableName(tableName);
    setDbSearch("");
    setDbSearchColumn("all");
    setDbSortBy(defaultSort);
    setDbSortDir(defaultDir);
    setDbDateFilterOn(false);
    setDbDateColumn(firstDateColumn);
    setDbDateFrom(todayDateString());
    setDbDateTo(todayDateString());
    setDbPage(1);
    setDbPageSize(200);
    setDbColumnFilters({});
    setDbOnlyUnused(false);
    await loadDbTable({
      tableName,
      page: 1,
      pageSize: 200,
      search: "",
      searchColumn: "all",
      sortBy: defaultSort,
      sortDir: defaultDir,
      dateFilterOn: false,
      dateColumn: firstDateColumn,
      dateFrom: todayDateString(),
      dateTo: todayDateString(),
      filters: {},
    });
  };

  const applyDbFilters = async () => {
    setDbPage(1);
    await loadDbTable({ page: 1 });
  };

  const clearDbFilters = async () => {
    setDbOnlyUnused(false);
    const table = dbTables.find(item => item.table_name === dbTableName || item.name === dbTableName);
    const columns = table?.columns || dbBrowser.columns || [];
    const defaultSort = table?.default_sort && columns.some(column => column.name === table.default_sort)
      ? table.default_sort
      : columns[0]?.name || "";
    const defaultDir = table?.default_dir || "DESC";
    const firstDateColumn = (table?.date_columns || dbBrowser.date_columns || [])[0] || "";
    setDbSearch("");
    setDbSearchColumn("all");
    setDbSortBy(defaultSort);
    setDbSortDir(defaultDir);
    setDbDateFilterOn(false);
    setDbDateColumn(firstDateColumn);
    setDbDateFrom(todayDateString());
    setDbDateTo(todayDateString());
    setDbPage(1);
    setDbColumnFilters({});
    await loadDbTable({
      page: 1,
      search: "",
      searchColumn: "all",
      sortBy: defaultSort,
      sortDir: defaultDir,
      dateFilterOn: false,
      dateColumn: firstDateColumn,
      dateFrom: todayDateString(),
      dateTo: todayDateString(),
      filters: {},
    });
  };

  const changeDbPage = async (nextPage) => {
    const normalized = Math.max(1, Math.min(Number(nextPage || 1), dbBrowser.total_pages || 1));
    setDbPage(normalized);
    await loadDbTable({ page: normalized });
  };

  const openDbEditor = async () => {
    setDbEditorOpen(true);
    let rows = dbTables.length ? dbTables : overview.db_tables || [];
    if (!dbTables.length) {
      rows = await loadDbTables();
    }
    if (!dbTableName && rows.length) {
      await openDbTable(rows[0].table_name || rows[0].name, rows);
    }
  };

  const selectDbRowKey = (row, rowIndex) => String(row.id ?? `${dbBrowser.page}-${rowIndex}`);

  const toggleDbRow = (row, rowIndex) => {
    const key = selectDbRowKey(row, rowIndex);
    setDbSelectedRows(current => ({ ...current, [key]: !current[key] }));
  };

  const toggleAllDbRows = () => {
    const rows = dbBrowser.rows || [];
    const allSelected = rows.length > 0 && rows.every((row, rowIndex) => dbSelectedRows[selectDbRowKey(row, rowIndex)]);
    if (allSelected) {
      setDbSelectedRows({});
      return;
    }
    setDbSelectedRows(Object.fromEntries(rows.map((row, rowIndex) => [selectDbRowKey(row, rowIndex), true])));
  };

  const applyDbColumnFilter = async (columnName, value) => {
    const nextFilters = { ...dbColumnFilters };
    const cleanValue = String(value || "").trim();
    if (cleanValue) nextFilters[columnName] = cleanValue;
    else delete nextFilters[columnName];
    setDbColumnFilters(nextFilters);
    setDbColumnMenu(null);
    setDbPage(1);
    await loadDbTable({ page: 1, filters: nextFilters });
  };

  const sortDbColumn = async (columnName, sortDir) => {
    setDbSortBy(columnName);
    setDbSortDir(sortDir);
    setDbColumnMenu(null);
    setDbPage(1);
    await loadDbTable({ page: 1, sortBy: columnName, sortDir });
  };

  const exportDbRows = () => {
    const exportColumns = (dbBrowser.columns || []).filter(column => column.visible !== false && Number(column.width || 100) !== 0);
    if (!dbBrowser.rows.length || !exportColumns.length) {
      toast.info("No DB rows to export.");
      return;
    }
    const headers = exportColumns.map(column => column.name);
    const csv = [
      headers.map(csvEscape).join(","),
      ...dbBrowser.rows.map(row => headers.map(header => csvEscape(row[header])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${dbBrowser.table_name || "db_table"}_page_${dbBrowser.page || 1}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const activeDbColumn = dbColumnMenu?.column || "";
  const selectedDbRowCount = Object.values(dbSelectedRows).filter(Boolean).length;
  const visibleDbColumns = (dbBrowser.columns || []).filter(column => column.visible !== false && Number(column.width || 100) !== 0);
  const dbInputSx = {
    minWidth: 130,
    "& .MuiInputBase-root": {
      bgcolor: dbEditorColors.card,
      color: dbEditorColors.text,
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: dbEditorColors.border },
    "& .MuiInputLabel-root": { color: dbEditorColors.muted },
    "& .MuiSvgIcon-root": { color: dbEditorColors.text },
    "& input": { color: dbEditorColors.text },
  };

  return (
    <Box data-build={pageBuildMarker} sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      {/* Everything the operator can do lives inside this one block (it renders
          reliably); the four tools are presented as a clean card hub. */}
      <Box>
        {/* Hero */}
        <Box sx={{
          borderRadius: 3, p: { xs: 2.5, md: 3.5 }, mb: 3, color: "#fff",
          background: "linear-gradient(135deg, #4a2c82 0%, #2f6fed 55%, #1abc9c 100%)",
          boxShadow: "0 10px 30px rgba(47,111,237,0.25)",
        }}>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.3 }}>
            🛍 Myntra Automation
          </Typography>
          <Typography sx={{ opacity: 0.9, mt: 0.5, maxWidth: 720 }}>
            Search &amp; open labels from the server, browse the live database, manage seals,
            and manage the uploaded Myntra / Flipkart / SKU print files — all in one place.
          </Typography>
        </Box>

        {/* Tool cards */}
        <Box sx={{
          display: "grid", gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
        }}>
          {[
            { key: "search", icon: "🔎", title: "Label Search", tone: "#2f6fed",
              desc: "Find a label by tracking or order id across the server archive and open the merged PDF.",
              cta: "Search labels", onClick: () => setLabelSearchOpen(true) },
            { key: "db", icon: "🗄", title: "Database", tone: "#9b59b6",
              desc: "Browse the live Myntra DB — orders, seals, schedules, run logs — auto-synced from the local app.",
              cta: "Open DB editor", onClick: openDbEditor },
            { key: "sealAdd", icon: "🏷", title: "Add Seals", tone: "#f39c12",
              desc: "Add seals to the pool by range or list; shows the live unused-seal count.",
              cta: "Add to pool", onClick: () => setSealAddOpen(true) },
            { key: "sealBarcode", icon: "🖨", title: "Seal Barcode PDF", tone: "#e67e22",
              desc: "Generate a printable Code128 barcode sheet (2 × 10 per 3″×5″ page).",
              cta: "Generate PDF", onClick: () => setSealBarcodeOpen(true) },
            { key: "fktCrop", icon: "✂️", title: "Flipkart Crop", tone: "#2874f0",
              desc: "Browse a raw Flipkart labels PDF → remove duplicates, group, auto-crop, save to server.",
              cta: "Crop & de-dup", onClick: () => setFlipkartCropOpen(true) },
            { key: "adv", icon: "🧾", title: "ADV", tone: "#8e44ad",
              desc: "Export a Myntra ADV Release-Order (RO/PO) PDF: pick account + amount, save to Downloads. History included.",
              cta: "Export RO/PO", onClick: () => navigate("/admin/myntra-adv") },
            { key: "files", icon: "📁", title: "Server Files", tone: "#1abc9c",
              desc: "Browse, search, upload and download the Myntra / Flipkart / SKU print files.",
              cta: "Open files", onClick: () => setFilesOpen(true) },
          ].map(t => (
            <Paper key={t.key} onClick={t.onClick} elevation={0}
              sx={{
                p: 2.5, borderRadius: 3, cursor: "pointer", height: "100%",
                border: "1px solid #e5e7eb", position: "relative", overflow: "hidden",
                transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
                "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 28px rgba(0,0,0,0.12)", borderColor: t.tone },
                "&:before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 4, background: t.tone },
              }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: 2, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 26, mb: 1.5, background: `${t.tone}1a`,
              }}>{t.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{t.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, minHeight: 60 }}>
                {t.desc}
              </Typography>
              <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); t.onClick(); }}
                sx={{ fontWeight: 800, bgcolor: t.tone, "&:hover": { bgcolor: t.tone, filter: "brightness(0.92)" } }}>
                {t.cta}
              </Button>
            </Paper>
          ))}
        </Box>

        {/* Dialogs — kept inside this rendered block so they always mount */}
        <Dialog open={sealAddOpen} onClose={() => setSealAddOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
            🏷 Add Seals
            <IconButton onClick={() => setSealAddOpen(false)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers><SealAddCard /></DialogContent>
        </Dialog>
        <Dialog open={sealBarcodeOpen} onClose={() => setSealBarcodeOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
            🖨 Seal Barcode PDF
            <IconButton onClick={() => setSealBarcodeOpen(false)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers><SealBarcodeCard /></DialogContent>
        </Dialog>
        <FlipkartCropDialog open={flipkartCropOpen} onClose={() => setFlipkartCropOpen(false)} />
        <ArchiveFilesDialog open={filesOpen} onClose={() => setFilesOpen(false)} />
        <LabelSearchDialog open={labelSearchOpen} onClose={() => setLabelSearchOpen(false)} />
      </Box>

      <Menu
        anchorEl={toolsAnchorEl}
        open={Boolean(toolsAnchorEl)}
        onClose={() => setToolsAnchorEl(null)}
      >
        {localToolsMenu.map((item, index) => (
          item.divider ? (
            <Divider key={`divider-${index}`} />
          ) : (
            <MenuItem
              key={item.type}
              disabled={item.type !== "open_tracking_search_dialog" && (Boolean(queueing) || !commandAllowed(item))}
              onClick={() => handleLocalToolAction(item)}
              sx={{ minWidth: 280, gap: 1.5, fontWeight: 700 }}
            >
              <Box component="span" sx={{ width: 24, textAlign: "center" }}>{item.icon}</Box>
              {item.title}
            </MenuItem>
          )
        ))}
      </Menu>

      <Dialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "82vh",
            bgcolor: dbEditorColors.bg,
            color: dbEditorColors.text,
            border: `1px solid ${dbEditorColors.border}`,
          },
        }}
      >
        <Box sx={{ bgcolor: dbEditorColors.header, px: 2, py: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: dbEditorColors.text }}>
              Search & Merge PDFs by Tracking ID
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTrackingDialogOpen(false)}
              sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 800 }}
            >
              Close
            </Button>
          </Stack>
        </Box>
        <DialogContent sx={{ p: 2, bgcolor: dbEditorColors.bg, display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0 }}>
          <Alert
            severity={browserWorker.machine_id ? "success" : "warning"}
            action={
              <Button size="small" onClick={detectBrowserWorker} sx={{ fontWeight: 900 }}>
                Check
              </Button>
            }
          >
            {browserWorker.machine_id
              ? `This browser PC detected: ${browserWorker.machine_name || browserWorker.machine_id}. Search & Merge will open here unless you choose another PC.`
              : "This browser PC local Myntra app is not detected. Start Myntra app on this PC, then click Check."}
          </Alert>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
            <FormControl size="small" sx={{ ...dbInputSx, minWidth: { xs: "100%", md: 360 } }}>
              <InputLabel>PDF opens on PC</InputLabel>
              <Select
                label="PDF opens on PC"
                value={targetMachineId}
                onChange={(event) => setTargetMachineId(event.target.value)}
              >
                <MenuItem value="" disabled>
                  {browserWorker.machine_id ? "This browser PC auto-selected" : "No browser PC detected"}
                </MenuItem>
                {targetOptions.map(option => (
                  <MenuItem key={option.value} value={option.value} disabled={!option.supported}>
                    {option.isBrowserPc ? "This browser PC - " : ""}{option.label} {option.supported ? "" : " - restart local app"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ color: dbEditorColors.muted, fontWeight: 800 }}>
              {targetMachineId
                ? `Selected: ${selectedTargetWorker?.label || targetMachineId}. Missing PDFs redownload on that PC before opening.`
                : "Start Myntra app on this PC so the website can target this exact browser PC."}
            </Typography>
          </Stack>

          <Box>
            <Typography sx={{ color: dbEditorColors.text, fontWeight: 900, mb: 0.5 }}>
              Enter tracking IDs
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={trackingTerms}
              onChange={(event) => setTrackingTerms(event.target.value)}
              placeholder="MYSP1405719613 DEF456789"
              sx={{
                "& .MuiInputBase-root": { bgcolor: dbEditorColors.card, color: dbEditorColors.text },
                "& textarea": { color: dbEditorColors.text, fontFamily: "Consolas, monospace", fontWeight: 800 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: dbEditorColors.border },
              }}
            />
          </Box>

          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Checkbox
              checked={trackingPartial}
              onChange={(event) => setTrackingPartial(event.target.checked)}
              sx={{ color: dbEditorColors.muted, p: 0.25 }}
            />
            <Typography sx={{ color: dbEditorColors.text, fontWeight: 900 }}>Partial Search</Typography>
            <Typography sx={{ color: dbEditorColors.muted }}>tick matches, then open selected</Typography>
            <Button
              variant="contained"
              onClick={runTrackingSearch}
              disabled={trackingLoading || trackingMerging}
              sx={{ ml: { xs: 0, md: "auto" }, fontWeight: 900 }}
            >
              {trackingLoading ? "Searching..." : trackingPartial ? "Search Partial" : "Search & Merge"}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => mergeTrackingRows()}
              disabled={trackingMerging || !trackingSearch.rows.some(row => trackingSelected[row.row_key])}
              sx={{ fontWeight: 900 }}
            >
              {trackingMerging ? "Merging..." : "Open Selected"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setTrackingTerms("");
                setTrackingSearch(defaultTrackingSearch);
                setTrackingSelected({});
                setTrackingLog([]);
              }}
              sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}
            >
              Clear
            </Button>
          </Stack>

          <Box sx={{ bgcolor: dbEditorColors.card, border: `1px solid ${dbEditorColors.border}`, p: 1, minHeight: 100, maxHeight: 150, overflow: "auto" }}>
            {trackingLog.length === 0 ? (
              <Typography sx={{ color: dbEditorColors.muted }}>Ready.</Typography>
            ) : trackingLog.map((line, index) => (
              <Typography
                key={`${line.at}-${index}`}
                sx={{
                  color: line.tone === "success" ? "#22c55e" : line.tone === "error" ? "#ef4444" : line.tone === "warn" ? "#f59e0b" : dbEditorColors.muted,
                  fontFamily: "Consolas, monospace",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {line.message}
              </Typography>
            ))}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TableContainer sx={{ height: "100%", bgcolor: dbEditorColors.card, border: `1px solid ${dbEditorColors.border}` }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Pick", "Tracking ID", "Tracking ID2", "Order ID", "Seller SKU", "Label", "Invoice", "Overlay Time"].map(head => (
                      <TableCell key={head} sx={{ bgcolor: dbEditorColors.header, color: dbEditorColors.text, fontWeight: 900, borderColor: dbEditorColors.border }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingSearch.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: dbEditorColors.muted, borderColor: dbEditorColors.border }}>
                        No search results yet.
                      </TableCell>
                    </TableRow>
                  ) : trackingSearch.rows.map(row => (
                    <TableRow key={row.row_key} sx={{ bgcolor: trackingSelected[row.row_key] ? dbEditorColors.selected : dbEditorColors.panel }}>
                      <TableCell sx={{ borderColor: dbEditorColors.border }}>
                        <Checkbox
                          checked={Boolean(trackingSelected[row.row_key])}
                          onChange={() => setTrackingSelected(current => ({ ...current, [row.row_key]: !current[row.row_key] }))}
                          disabled={!row.label_uploaded && !row.invoice_uploaded}
                          sx={{ color: dbEditorColors.muted, p: 0.25 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}>{row.tracking_id || "-"}</TableCell>
                      <TableCell sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border }}>{row.tracking_id2 || "-"}</TableCell>
                      <TableCell sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border }}>{row.order_id || "-"}</TableCell>
                      <TableCell sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.seller_sku || "-"}</TableCell>
                      <TableCell sx={{ color: row.label_uploaded ? "#22c55e" : "#f59e0b", borderColor: dbEditorColors.border, fontWeight: 900 }}>
                        {row.label_uploaded ? "Yes" : "Missing"}
                      </TableCell>
                      <TableCell sx={{ color: row.invoice_uploaded ? "#22c55e" : "#f59e0b", borderColor: dbEditorColors.border, fontWeight: 900 }}>
                        {row.invoice_uploaded ? "Yes" : "Missing"}
                      </TableCell>
                      <TableCell sx={{ color: dbEditorColors.muted, borderColor: dbEditorColors.border }}>{row.overlay_time || row.status_date || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>

      {showDashboardBody && (
      <>
      {overview.migration_required && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Database migration is pending.
        </Alert>
      )}

      {!overview.worker_token_configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Worker token is not configured on the server.
        </Alert>
      )}

      <Alert severity={overview.command_execution_enabled ? "success" : "info"} sx={{ mb: 2 }}>
        {overview.phase5_operator_commands_enabled || overview.phase4_risky_commands_enabled
          ? `Phase 5 guarded operator commands are enabled. Schedule, login/profile, merge, repair, and in-transit tools execute on the local app only. Running commands time out after ${overview.command_stale_minutes || 10} minutes.`
          : overview.command_execution_enabled
          ? `Phase 3 safe commands are enabled: health check, backup DB, clean profiles, and sync DB summary. Operator commands stay locked. Running commands time out after ${overview.command_stale_minutes || 10} minutes.`
          : "Cloud commands are disabled. Heartbeat, status, logs, schedules, and run summaries work from the local app."}
      </Alert>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(5, 1fr)" }, gap: 1.5, mb: 2 }}>
        <StatCard icon={<FaServer />} title="Server" value={overview.server_runtime} sub="Node backend" />
        <StatCard icon={<FaDatabase />} title="DB" value={overview.database} tone="success" sub={`${overview.db_tables.length} bridge tables`} />
        <StatCard icon={<FaLaptopCode />} title="Workers" value={`${onlineWorkers}/${overview.workers.length}`} tone={onlineWorkers ? "success" : "warning"} sub="Local Mac/Windows clients" />
        <StatCard icon={<FaClock />} title="Cloud Plan" value={`${overview.cloud_accounts.length} accounts`} tone="primary" sub={`${activeSchedules}/${overview.cloud_schedules.length} schedules active | ${overview.app_date || "-"}`} />
        <StatCard icon={<FaPlay />} title="Commands" value={pendingCommands} tone={pendingCommands ? "warning" : "success"} sub="Pending or running" />
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ xs: "stretch", lg: "center" }} sx={{ mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", lg: 280 } }}>
            <InputLabel>Target Worker</InputLabel>
            <Select label="Target Worker" value={targetMachineId} onChange={(event) => setTargetMachineId(event.target.value)}>
              <MenuItem value="">Any online worker</MenuItem>
              {targetOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Account" value={accountName} onChange={(event) => setAccountName(event.target.value)} placeholder="HAMBERG / SLOWLORIS" />
          <TextField size="small" label="Schedule Time" value={scheduledTime} onChange={(event) => setScheduledTime(event.target.value)} placeholder="06:30 / 11:30" />
          <TextField size="small" label="Cutoff" value={cutoffTime} onChange={(event) => setCutoffTime(event.target.value)} placeholder="04:30 PM" />
          <TextField size="small" label="Profiles" value={profileRange} onChange={(event) => setProfileRange(event.target.value)} placeholder="1-15" />
          <TextField size="small" label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} sx={{ minWidth: { xs: "100%", lg: 220 } }} />
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)", xl: "repeat(7, 1fr)" }, gap: 1 }}>
          {commandCards.map(card => (
            <Button
              key={card.type}
              variant="contained"
              color={card.color}
              startIcon={card.icon}
              onClick={() => queueCommand(card)}
              disabled={Boolean(queueing) || !commandAllowed(card)}
              sx={{ justifyContent: "flex-start", minHeight: 44, fontWeight: 800 }}
            >
              {queueing === card.type ? "Queueing..." : commandAllowed(card) ? card.title : `${card.title} Locked`}
            </Button>
          ))}
        </Box>

        {!overview.workers.length && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Commands will remain pending until the local Myntra worker connects from the Mac/Windows machine.
          </Alert>
        )}
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "0.9fr 1.1fr" }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Cloud Accounts
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Seller</TableCell>
                  <TableCell>Folder</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Extra Profiles</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.cloud_accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">No cloud accounts loaded.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.cloud_accounts.map(account => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 900 }}>{account.account_label || account.account_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{account.account_name}</Typography>
                    </TableCell>
                    <TableCell>{account.seller_id || "-"}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{account.brand_folder || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">{account.pdf_prefix || ""}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{account.primary_profile || "-"}</TableCell>
                    <TableCell>
                      {Array.isArray(account.extra_profiles) && account.extra_profiles.length
                        ? `${account.extra_profiles.length} (${account.extra_profiles[0]}-${account.extra_profiles[account.extra_profiles.length - 1]})`
                        : "-"}
                    </TableCell>
                    <TableCell><Chip size="small" color={Number(account.is_active) ? "success" : "default"} label={Number(account.is_active) ? "ACTIVE" : "OFF"} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Cloud Schedules
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Cutoff</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.cloud_schedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">No schedules loaded.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.cloud_schedules.map(schedule => (
                  <TableRow key={schedule.id}>
                    <TableCell sx={{ fontWeight: 900 }}>{schedule.scheduled_time}</TableCell>
                    <TableCell>{schedule.account_label || schedule.account_name}</TableCell>
                    <TableCell>{schedule.cutoff_time || "-"}</TableCell>
                    <TableCell>
                      {yesNoChip(schedule.pre_generate_current_sku, "SKU")}
                      {yesNoChip(schedule.post_merge_all, "Merge")}
                      {yesNoChip(schedule.post_print_all, "Print")}
                      {yesNoChip(schedule.post_in_transit_download, "Transit")}
                      {yesNoChip(schedule.post_in_transit_otc, "OTC")}
                    </TableCell>
                    <TableCell><Chip size="small" color={Number(schedule.is_active) ? "success" : "default"} label={Number(schedule.is_active) ? "ON" : "OFF"} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "0.85fr 1.15fr" }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Today Summary
          </Typography>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell>Success</TableCell>
                  <TableCell>Errors</TableCell>
                  <TableCell>PDFs</TableCell>
                  <TableCell>Last Run</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.today_run_summary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">No cloud run summary for today yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.today_run_summary.map(row => (
                  <TableRow key={row.account_name}>
                    <TableCell sx={{ fontWeight: 900 }}>{row.account_name}</TableCell>
                    <TableCell>{formatNumber(row.run_count)}</TableCell>
                    <TableCell>{formatNumber(row.orders_success)}</TableCell>
                    <TableCell>{formatNumber(row.orders_error)}</TableCell>
                    <TableCell>{formatNumber(row.pdfs_saved)}</TableCell>
                    <TableCell>{formatDate(row.last_run_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Recent Runs
          </Typography>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Success</TableCell>
                  <TableCell>PDFs</TableCell>
                  <TableCell>Machine</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.recent_run_summaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No cloud run records yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.recent_run_summaries.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.run_date || "-"}</TableCell>
                    <TableCell>{row.scheduled_time || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.account_name}</TableCell>
                    <TableCell><Chip size="small" color={statusColor(row.status)} label={String(row.status || "unknown").toUpperCase()} /></TableCell>
                    <TableCell>{formatNumber(row.orders_success)}</TableCell>
                    <TableCell>{formatNumber(row.pdfs_saved)}</TableCell>
                    <TableCell>{row.source_machine_id || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Worker Status
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Machine</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Profiles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cloud Commands</TableCell>
                  <TableCell>Current Task</TableCell>
                  <TableCell>Last Seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.workers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No local worker connected.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.workers.map(worker => (
                  <TableRow key={worker.id || worker.machine_id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{worker.machine_name || worker.machine_id}</Typography>
                      <Typography variant="caption" color="text.secondary">{worker.machine_id}</Typography>
                    </TableCell>
                    <TableCell>{worker.platform || "-"}</TableCell>
                    <TableCell>{worker.profile_count || 0}</TableCell>
                    <TableCell><Chip size="small" color={statusColor(worker.status)} label={String(worker.status || "unknown").toUpperCase()} /></TableCell>
                    <TableCell>
                      {Array.isArray(worker.supported_command_types) && worker.supported_command_types.includes("merge_all_pdfs")
                        ? <Chip size="small" color="success" label="PHASE 5" />
                        : Array.isArray(worker.supported_command_types) && worker.supported_command_types.includes("manual_schedule_run")
                        ? <Chip size="small" color="success" label="PHASE 4" />
                        : <Chip size="small" color="default" variant="outlined" label="SAFE ONLY" />}
                    </TableCell>
                    <TableCell>{worker.current_task || "-"}</TableCell>
                    <TableCell>{formatDate(worker.last_seen_at || worker.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      </>
      )}

      <Dialog
        open={dbEditorOpen}
        onClose={() => setDbEditorOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(1560px, 96vw)",
            height: "88vh",
            m: 1,
            bgcolor: dbEditorColors.bg,
            color: dbEditorColors.text,
            border: `1px solid ${dbEditorColors.border}`,
          },
        }}
      >
        <Box sx={{ bgcolor: dbEditorColors.header, px: 2, py: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: dbEditorColors.text }}>
                Database Editor - Myntra Automation Suite
              </Typography>
              <Typography variant="caption" sx={{ color: dbEditorColors.muted }}>
                {dbBrowser.source_db_path
                  ? `${dbBrowser.source_db_path} | ${dbBrowser.machine_id || "local machine"}`
                  : "Waiting for local myntra.db sync from the desktop app."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<FaDatabase />}
                onClick={() => queueCommand(commandCards.find(card => card.type === "sync_local_db_full"))}
                disabled={Boolean(queueing) || !commandAllowed(commandCards.find(card => card.type === "sync_local_db_full"))}
                sx={{ fontWeight: 800 }}
              >
                Sync Now
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<FaSyncAlt />}
                onClick={() => dbTableName ? loadDbTable() : loadDbTables()}
                disabled={dbLoading}
                sx={{ fontWeight: 800 }}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDbEditorOpen(false)}
                sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 800 }}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0, bgcolor: dbEditorColors.bg }}>
          <Tabs
            value={dbTableName || false}
            onChange={(_event, value) => openDbTable(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              bgcolor: dbEditorColors.panel,
              borderBottom: `1px solid ${dbEditorColors.border}`,
              "& .MuiTab-root": {
                minHeight: 42,
                color: dbEditorColors.muted,
                fontWeight: 900,
                textTransform: "none",
              },
              "& .Mui-selected": { color: dbEditorColors.text },
              "& .MuiTabs-indicator": { bgcolor: dbEditorColors.accent },
            }}
          >
            {dbStatsRows.map(table => (
              <Tab
                key={table.table_name || table.name}
                value={table.table_name || table.name}
                label={`${table.label || table.table_name || table.name} (${table.error || formatNumber(table.row_count)})`}
              />
            ))}
          </Tabs>

          <Box sx={{ bgcolor: dbEditorColors.panel, borderBottom: `1px solid ${dbEditorColors.border}`, px: 1, py: 0.75 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ color: dbEditorColors.muted, fontWeight: 800 }}>
                Search:
              </Typography>
              <FormControl size="small" sx={{ ...dbInputSx, minWidth: 150 }}>
                <Select value={dbSearchColumn} onChange={(event) => setDbSearchColumn(event.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {(dbBrowser.searchable_columns || []).map(column => (
                    <MenuItem key={column} value={column}>{titleizeColumn(column)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                value={dbSearch}
                onChange={(event) => setDbSearch(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") applyDbFilters(); }}
                placeholder="Search"
                sx={{ ...dbInputSx, minWidth: 210 }}
              />
              <Button size="small" variant="contained" onClick={applyDbFilters} disabled={!dbTableName || dbLoading} sx={{ fontWeight: 900 }}>
                Search
              </Button>
              <Button size="small" variant="outlined" onClick={clearDbFilters} disabled={!dbTableName || dbLoading} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}>
                Clear
              </Button>

              {dbBrowser.unused_count != null && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: dbEditorColors.border, mx: 0.5 }} />
                  <Chip size="small" color="success"
                    label={`Unused seals: ${formatNumber(dbBrowser.unused_count)}`}
                    sx={{ fontWeight: 800 }} />
                  <FormControlLabel
                    sx={{ color: dbEditorColors.text, m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={dbOnlyUnused}
                        sx={{ color: dbEditorColors.muted }}
                        onChange={(event) => {
                          const on = event.target.checked;
                          setDbOnlyUnused(on);
                          setDbPage(1);
                          loadDbTable({ onlyUnused: on, page: 1 });
                        }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Unused only</Typography>}
                  />
                </>
              )}

              <Divider orientation="vertical" flexItem sx={{ borderColor: dbEditorColors.border, mx: 0.5 }} />

              <Checkbox
                checked={dbDateFilterOn}
                onChange={async (event) => {
                  const enabled = event.target.checked;
                  setDbDateFilterOn(enabled);
                  setDbPage(1);
                  await loadDbTable({ page: 1, dateFilterOn: enabled });
                }}
                size="small"
                sx={{ color: dbEditorColors.muted, p: 0.25 }}
              />
              <Typography variant="body2" sx={{ color: dbEditorColors.text, fontWeight: 800 }}>
                Date:
              </Typography>
              <FormControl size="small" sx={{ ...dbInputSx, minWidth: 150 }}>
                <Select value={dbDateColumn} onChange={(event) => setDbDateColumn(event.target.value)} disabled={!dbBrowser.date_columns.length}>
                  {(dbBrowser.date_columns || []).map(column => (
                    <MenuItem key={column} value={column}>{titleizeColumn(column)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="date" value={dbDateFrom} onChange={(event) => setDbDateFrom(event.target.value)} sx={{ ...dbInputSx, minWidth: 145 }} disabled={!dbDateFilterOn} />
              <Typography variant="body2" sx={{ color: dbEditorColors.muted }}>to</Typography>
              <TextField size="small" type="date" value={dbDateTo} onChange={(event) => setDbDateTo(event.target.value)} sx={{ ...dbInputSx, minWidth: 145 }} disabled={!dbDateFilterOn} />
              <Button
                size="small"
                variant="outlined"
                onClick={async () => {
                  const today = todayDateString();
                  setDbDateFrom(today);
                  setDbDateTo(today);
                  setDbDateFilterOn(true);
                  setDbPage(1);
                  await loadDbTable({ page: 1, dateFilterOn: true, dateFrom: today, dateTo: today });
                }}
                sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}
              >
                Today
              </Button>

              <Divider orientation="vertical" flexItem sx={{ borderColor: dbEditorColors.border, mx: 0.5 }} />

              <Button size="small" variant="outlined" startIcon={<FaDownload />} onClick={exportDbRows} disabled={!dbBrowser.rows.length} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}>
                Export
              </Button>
              <Button size="small" variant="outlined" startIcon={<FaSyncAlt />} onClick={() => loadDbTable()} disabled={!dbTableName || dbLoading} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900 }}>
                Refresh
              </Button>

              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: "auto" }}>
                <IconButton size="small" onClick={() => changeDbPage(dbPage - 1)} disabled={!dbTableName || dbLoading || dbPage <= 1} sx={{ color: dbEditorColors.text }}>
                  <FaChevronLeft />
                </IconButton>
                <Typography variant="body2" sx={{ color: dbEditorColors.muted, minWidth: 126, textAlign: "center", fontWeight: 800 }}>
                  Page {dbBrowser.page}/{dbBrowser.total_pages}
                </Typography>
                <IconButton size="small" onClick={() => changeDbPage(Number(dbPage) + 1)} disabled={!dbTableName || dbLoading || dbPage >= dbBrowser.total_pages} sx={{ color: dbEditorColors.text }}>
                  <FaChevronRight />
                </IconButton>
                <FormControl size="small" sx={{ ...dbInputSx, minWidth: 92 }}>
                  <Select
                    value={dbPageSize}
                    onChange={(event) => {
                      const size = Number(event.target.value);
                      setDbPageSize(size);
                      setDbPage(1);
                      loadDbTable({ page: 1, pageSize: size });
                    }}
                  >
                    {[50, 100, 200].map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 0.75 }}>
            <TableContainer sx={{ height: "100%", border: `1px solid ${dbEditorColors.border}`, bgcolor: dbEditorColors.card }}>
              <Table stickyHeader size="small" sx={{ tableLayout: "auto", minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 42,
                        bgcolor: dbEditorColors.header,
                        color: dbEditorColors.text,
                        borderColor: dbEditorColors.border,
                        p: 0.25,
                        textAlign: "center",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={Boolean(dbBrowser.rows.length) && dbBrowser.rows.every((row, rowIndex) => dbSelectedRows[selectDbRowKey(row, rowIndex)])}
                        indeterminate={selectedDbRowCount > 0 && selectedDbRowCount < dbBrowser.rows.length}
                        onChange={toggleAllDbRows}
                        sx={{ color: dbEditorColors.muted, p: 0.25 }}
                      />
                    </TableCell>
                    {visibleDbColumns.map(column => (
                      <TableCell
                        key={column.name}
                        sx={{
                          bgcolor: dbSortBy === column.name ? "#1e3a5f" : dbEditorColors.header,
                          color: dbEditorColors.text,
                          borderColor: dbEditorColors.border,
                          whiteSpace: "nowrap",
                          fontWeight: 900,
                          p: 0,
                          minWidth: Number(column.width || 100) || (/json|payload|result|metadata|notes|path/i.test(column.name) ? 220 : 110),
                        }}
                      >
                        <Button
                          fullWidth
                          onClick={(event) => {
                            setDbColumnMenu({ anchorEl: event.currentTarget, column: column.name });
                            setDbColumnFilterDraft(dbColumnFilters[column.name] || "");
                          }}
                          sx={{
                            color: dbEditorColors.text,
                            justifyContent: "flex-start",
                            textTransform: "none",
                            fontWeight: 900,
                            px: 1,
                            py: 0.5,
                            minHeight: 30,
                          }}
                        >
                          {titleizeColumn(column.name)}
                          {dbSortBy === column.name ? (dbSortDir === "DESC" ? " ▼" : " ▲") : " ▾"}
                          {dbColumnFilters[column.name] ? " *" : ""}
                        </Button>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!visibleDbColumns.length ? (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ color: dbEditorColors.muted, borderColor: dbEditorColors.border }}>
                        Open DB from the button above, then choose a table tab.
                      </TableCell>
                    </TableRow>
                  ) : dbBrowser.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(2, visibleDbColumns.length + 1)} sx={{ color: dbEditorColors.muted, borderColor: dbEditorColors.border }}>
                        No rows match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : dbBrowser.rows.map((row, rowIndex) => {
                    const rowKey = selectDbRowKey(row, rowIndex);
                    const checked = Boolean(dbSelectedRows[rowKey]);
                    return (
                      <TableRow key={`${dbBrowser.table_name}-${rowKey}`} sx={{ bgcolor: checked ? dbEditorColors.selected : rowIndex % 2 ? dbEditorColors.even : dbEditorColors.panel }}>
                        <TableCell sx={{ p: 0.25, textAlign: "center", borderColor: dbEditorColors.border }}>
                          <Checkbox size="small" checked={checked} onChange={() => toggleDbRow(row, rowIndex)} sx={{ color: dbEditorColors.muted, p: 0.25 }} />
                        </TableCell>
                        {visibleDbColumns.map(column => (
                          <TableCell
                            key={column.name}
                            sx={{
                              color: dbEditorColors.text,
                              borderColor: dbEditorColors.border,
                              maxWidth: /json|payload|result|metadata|notes|path/i.test(column.name) ? 360 : 220,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: /json|payload|result|metadata|notes|path/i.test(column.name) ? "Consolas, monospace" : "inherit",
                              py: 0.55,
                            }}
                            title={row[column.name] === null || row[column.name] === undefined ? "" : String(row[column.name])}
                          >
                            {shortCell(row[column.name], 120)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ bgcolor: dbEditorColors.panel, borderTop: `1px solid ${dbEditorColors.border}`, px: 1.5, py: 0.75 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1}>
              <Typography variant="caption" sx={{ color: dbEditorColors.muted }}>
                Showing {dbBrowser.rows.length ? `${formatNumber((dbBrowser.page - 1) * dbBrowser.page_size + 1)}-${formatNumber((dbBrowser.page - 1) * dbBrowser.page_size + dbBrowser.rows.length)}` : "0"} of {formatNumber(dbBrowser.total)} rows | {dbBrowser.table_name || "No table"} | Page {dbBrowser.page}
              </Typography>
              <Typography variant="caption" sx={{ color: dbEditorColors.accent, fontWeight: 900 }}>
                {selectedDbRowCount} selected
              </Typography>
            </Stack>
          </Box>
        </DialogContent>

        <Popover
          open={Boolean(dbColumnMenu)}
          anchorEl={dbColumnMenu?.anchorEl || null}
          onClose={() => setDbColumnMenu(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              width: 300,
              bgcolor: dbEditorColors.bg,
              color: dbEditorColors.text,
              border: `1px solid ${dbEditorColors.border}`,
              p: 1,
            },
          }}
        >
          <Typography sx={{ fontWeight: 900, color: dbEditorColors.accent, mb: 1 }}>
            {titleizeColumn(activeDbColumn)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button size="small" variant="outlined" onClick={() => sortDbColumn(activeDbColumn, "ASC")} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 800, flex: 1 }}>
              Sort A-Z
            </Button>
            <Button size="small" variant="outlined" onClick={() => sortDbColumn(activeDbColumn, "DESC")} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 800, flex: 1 }}>
              Sort Z-A
            </Button>
          </Stack>
          <TextField
            size="small"
            fullWidth
            label="Filter contains"
            value={dbColumnFilterDraft}
            onChange={(event) => setDbColumnFilterDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") applyDbColumnFilter(activeDbColumn, dbColumnFilterDraft); }}
            sx={{ ...dbInputSx, mb: 1 }}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => applyDbColumnFilter(activeDbColumn, dbColumnFilterDraft)} sx={{ fontWeight: 900, flex: 1 }}>
              Apply
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyDbColumnFilter(activeDbColumn, "")} sx={{ color: dbEditorColors.text, borderColor: dbEditorColors.border, fontWeight: 900, flex: 1 }}>
              Clear
            </Button>
          </Stack>
        </Popover>
      </Dialog>

      {showDashboardBody && (
      <>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
          Command Queue
        </Typography>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Command</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payload</TableCell>
                <TableCell>Result / Error</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.recent_commands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary">No commands queued.</Typography>
                  </TableCell>
                </TableRow>
              ) : overview.recent_commands.map(command => (
                <TableRow key={command.id}>
                  <TableCell>{command.id}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>{command.command_type}</TableCell>
                  <TableCell>{command.target_machine_id || command.claimed_by_machine_id || "Any"}</TableCell>
                  <TableCell><Chip size="small" color={statusColor(command.status)} label={String(command.status || "-").toUpperCase()} /></TableCell>
                  <TableCell sx={{ maxWidth: 420, whiteSpace: "normal", wordBreak: "break-word" }}>{prettyPayload(command.payload)}</TableCell>
                  <TableCell sx={{ maxWidth: 420, whiteSpace: "normal", wordBreak: "break-word" }}>{prettyResult(command.result, command.error_message)}</TableCell>
                  <TableCell>{formatDate(command.created_at)}</TableCell>
                  <TableCell>{formatDate(command.completed_at)}</TableCell>
                  <TableCell>
                    {["pending", "running"].includes(String(command.status || "").toLowerCase()) ? (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<FaTimesCircle />}
                        disabled={cancellingCommand === String(command.id)}
                        onClick={() => cancelCommand(command)}
                        sx={{ fontWeight: 800 }}
                      >
                        {cancellingCommand === String(command.id) ? "Cancelling" : "Cancel"}
                      </Button>
                    ) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Recent Worker Events
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Machine</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.recent_events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary">No worker events.</Typography>
                    </TableCell>
                  </TableRow>
                ) : overview.recent_events.map(event => (
                  <TableRow key={event.id}>
                    <TableCell>{formatDate(event.created_at)}</TableCell>
                    <TableCell>{event.machine_id}</TableCell>
                    <TableCell><Chip size="small" color={statusColor(event.severity)} label={event.event_type} /></TableCell>
                    <TableCell>{event.message || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            API Endpoints
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Method</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(overview.endpoints || []).map(endpoint => (
                  <TableRow key={`${endpoint.method}-${endpoint.path}`}>
                    <TableCell><Chip size="small" label={endpoint.method} color={endpoint.method === "POST" ? "primary" : "default"} /></TableCell>
                    <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{endpoint.path}</TableCell>
                    <TableCell>{endpoint.purpose}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      </>
      )}
    </Box>
  );
}
